import classNames from "classnames";
import { useCallback, useEffect, useRef } from "react";
import $ from "jquery";
import styles from "./UpdatesPopup.module.scss";
import AnimatedPopup from "../AnimatedComponents/AnimatedPopup";
import { useDispatch, useSelector } from "react-redux";
import { getDitaContent, selectDitaContent } from "../Content/ContentSlice";
import { useTableOfContents } from "../../hooks/tableOfContentsHooks/useTableOfContents";
import useAppModernisation from "../../hooks/useAppModernisation";
import colours from "../AppTopicBanner/apptopicbanner.module.scss";
import { selectDitaPath } from "../../features/counter/counterSlice";
import { setUpdateContent } from "../../features/topic/ContentSideBarSlice";
import { useUpdatesClickAction } from "../../hooks/updatesHooks/useUpdatesClickAction";
import useDebouncedMutationEffect from "../../hooks/useDebouncedMutationEffect";
import { extractTableFigureNumber } from "../../features/counter/prepareToView";

const UpdatesPopup = ({ isExpanded, expandSideBar }) => {
  const tableOfContents = useTableOfContents();
  const content = useSelector(selectDitaContent);
  const { isMobileView } = useAppModernisation();
  const ditapath = useSelector(selectDitaPath);
  const contentRef = useRef(null);
  const { clickAction } = useUpdatesClickAction(contentRef, expandSideBar);
  const handlePopupContentMutation = useCallback(
    (root) => {
      clickAction(root);
      enrichRenderedXrefAnchors(root);
    },
    [clickAction],
  );
  useDebouncedMutationEffect(contentRef, handlePopupContentMutation, [isExpanded], 100);

  let updates = [];
  const dispatch = useDispatch();
  const updatesList = getDitaContent("Updates/Updates_Reltable.ditamap", content, dispatch);
  const topLevelGuideline = tableOfContents?.tableOfContents?.href
    ? tableOfContents?.tableOfContents?.href
    : tableOfContents?.tableOfContents?.id;

  // Reset the sidebar "Topic updates" preview whenever the active topic
  // changes, so a previous topic's update text doesn't linger when the new
  // topic has no updates.
  useEffect(() => {
    dispatch(setUpdateContent({ updateContent: "" }));
  }, [dispatch, topLevelGuideline]);

  useEffect(() => {
    if (!isExpanded || !contentRef.current) return;
    enrichRenderedXrefAnchors(contentRef.current);
  }, [isExpanded, topLevelGuideline]);

  if (updatesList && topLevelGuideline) {
    updates.push(getUpdates(updatesList, decodeURIComponent(topLevelGuideline), content, dispatch));
  }

  return (
    <div>
      <AnimatedPopup show={isExpanded} direction={isMobileView ? "bottom" : "right"}>
        <div className={classNames("size-20 semibold", styles.updatePopup)}>
          <div
            className={classNames(styles.headerBar, {
              [colours[ditapath?.replace("/", "")]]: isMobileView,
            })}
          >
            <div className={styles.updatesHeader}>Topic updates</div>
            <i
              className={classNames("tg-icons-Cross size-24", styles.closeIcon)}
              onClick={expandSideBar}
            ></i>
          </div>
          <div className={styles.contributors} ref={contentRef}>
            {updates && <div>{updates}</div>}
          </div>
        </div>
      </AnimatedPopup>
      <div onClick={expandSideBar} className={isExpanded ? styles.greyedOut : ""}></div>
    </div>
  );
};

export default UpdatesPopup;

function getUpdates(updatesList, topLevelGuideline, content, dispatch) {
  let updatesSet = false;
  const updateRows = updatesList.getElementsByTagName("relrow");
  let formattedUpdates = [];
  let previousDate = "";
  let updateCountForDate = 0;

  Array.from(updateRows).forEach((row) => {
    const topicCell = row.children[1];
    const updateCell = row.children[0];
    if (!topicCell || !updateCell) return;

    const matchesTopic = Array.from(topicCell.children).some((topic) =>
      topic.attributes?.href?.value?.includes(topLevelGuideline),
    );

    if (!matchesTopic) return;

    // Iterate ALL update files in the first relcell (was previously only [0])
    Array.from(updateCell.children).forEach((updateRef) => {
      const url = updateRef.attributes?.href?.value;
      if (!url) return;
      const update = getDitaContent("Updates/" + url, content, dispatch);
      if (!update) return;

      const result = getUpdateInfo(
        update,
        formattedUpdates,
        updatesSet,
        previousDate,
        updateCountForDate,
        dispatch,
        url,
      );
      if (result.date !== previousDate) {
        updateCountForDate = 1;
      } else {
        updateCountForDate++;
      }
      previousDate = result.date;
      updatesSet = true;
    });
  });

  return formattedUpdates;
}

function getUpdateInfo(
  update,
  formattedUpdates,
  updatesSet,
  previousDate,
  updateCountForDate,
  dispatch,
  url,
) {
  const updateDate = update.getElementsByTagName("created")[0];
  const date = new Date(updateDate?.attributes?.date?.value).toLocaleString("default", {
    month: "long",
    year: "numeric",
  });
  if (previousDate !== date && date !== "Invalid Date") {
    formattedUpdates.push(
      <div key={`header-${url}`} className={classNames("size-16 bold", styles.dateHeader)}>
        Update: {date}
      </div>,
    );
  }

  // Add separator between updates of the same date (but not before the first one)
  if (previousDate === date && updateCountForDate > 0) {
    formattedUpdates.push(<hr key={`sep-${url}`} className={styles.updateSeparator} />);
  }

  const body = update.getElementsByTagName("conbody")[0];
  const xrefs = body.getElementsByTagName("xref");
  if (xrefs) {
    Array.from(xrefs).forEach((xref) => {
      if (xref?.attributes?.outputclass?.value?.includes("hidden")) {
        xref.setAttribute("style", "text-decoration: none; cursor: text; color: #414042;");
      }
      // Resolve reflective link text for empty xrefs (e.g. figure/table
      // references) the same way the main content viewer does in
      // features/counter/prepareToView.js -> PerpareXrefs(): look up the
      // matching topicref in the rendered TOC (#tg_toc_id) and pull its
      // ditatitle, then trim figure/table titles to "Figure X.Y" /
      // "Table X.Y" via the shared extractTableFigureNumber helper.
      populateReflectiveXrefText(xref);
    });
  }

  // The serializer keeps empty xrefs as self-closing tags
  // (`<xref href="..."/>`). The HTML parser used by dangerouslySetInnerHTML
  // does not honour self-closing syntax on unknown elements, so it treats
  // the tag as unclosed and absorbs the rest of the paragraph into the
  // link. Normalize to an explicit open/close pair to prevent that.
  const html = sanitiseSelfClosingXrefs(body.innerHTML);
  formattedUpdates.push(
    <div
      key={`body-${url}`}
      className={classNames("size-14 medium", styles.topicUpdateText)}
      dangerouslySetInnerHTML={{ __html: html }}
    ></div>,
  );
  if (!updatesSet) {
    dispatch(setUpdateContent({ updateContent: html }));
  }
  return { date };
}

// Cache resolved text per raw DITA href to survive transient TOC re-mounts.
const reflectiveTextCache = new Map();

// Mirror of prepareToView.js → PerpareXrefs(): strip the DITA path prefix,
// take the trailing anchor id (e.g. "fig-2623"), look it up in the rendered
// TOC, and trim to "Figure X.Y" / "Table X.Y" via extractTableFigureNumber.
function resolveXrefText(rawHref) {
  if (!rawHref) return "";
  const cached = reflectiveTextCache.get(rawHref);
  if (cached) return cached;

  let lookupLink = rawHref.includes("../../")
    ? rawHref.replace(/^(\.\.\/)+[^/]+\//, "")
    : rawHref.replace(/^\.\.\//, "");
  const parts = lookupLink.split("#");
  if (parts.length < 2) return "";
  const targetPart = parts[1] || "";
  if (!/fig-|-fig|table-|-tbl/i.test(targetPart)) return "";

  const tnfTocRef = targetPart.split("/").pop();
  const tocText = $('#tg_toc_id [href$="' + tnfTocRef + '"]:eq(0)').text();
  if (!tocText) return "";

  const resolved = extractTableFigureNumber(tocText);
  reflectiveTextCache.set(rawHref, resolved);
  return resolved;
}

function populateReflectiveXrefText(xref) {
  if (!xref || (xref.textContent || "").trim()) return;
  const rawHref = xref.getAttribute?.("href") || "";
  const resolved = resolveXrefText(rawHref);
  if (resolved) xref.textContent = resolved;
}

function enrichRenderedXrefAnchors(root) {
  if (!root) return;
  root
    .querySelectorAll("a.xrefLink[unformattedhref], a.xrefLink[unformattedHref]")
    .forEach((anchor) => {
      const rawHref =
        anchor.getAttribute("unformattedhref") || anchor.getAttribute("unformattedHref") || "";
      if (!rawHref) return;
      const text = (anchor.textContent || "").trim();
      if (text && !/^here(\s*\.\.\.)?$/i.test(text)) return;
      const resolved = resolveXrefText(rawHref);
      if (resolved) anchor.textContent = resolved;
    });
}

function sanitiseSelfClosingXrefs(html) {
  if (!html || typeof html !== "string") return html;
  return html.replace(/<(xref|fn|term|keyword|cite|ph)\b([^>]*)\/>/gi, "<$1$2></$1>");
}
